import { ReactElement, useEffect, useState } from "react";
import { httpDelete, httpGet, httpPost } from "../../../modules/Backend";
import { TableVirtuoso } from "react-virtuoso";
import { Button, TabPanel } from "react-aria-components";
import iconPlus from "../../../assets/icons/icon-plus-white.svg";
import iconEdit from "../../../assets/icons/icon-pen-blue.svg";
import iconDownload from "../../../assets/icons/icon-download-white.svg";
import iconDelete from "../../../assets/icons/icon-bin-red.svg";
import iconInfoBlue from "../../../assets/icons/icon-exclamation-blue.svg"
import { useNavigate, useParams } from "react-router";
import * as Navigation from "../../../modules/Navigation";
import GetSchema from "../entityComponents/Schema";
import { modalSignal } from "../Modal/ModalContainer";
import ConfirmModal, { ConfirmType } from "../Modal/ConfirmModal";
import { HttpStatusCode } from "axios";
import { SingularLowerCase, UpperCase } from "../../../helpers/EntityNameHelper";
import { IFilter } from "../../../hooks/UseFilters";
import { getCellComponent } from "./List";
import { globalUpdateResultsSignal } from "../layout/Layout";
import { useSignals } from "@preact/signals-react/runtime";
import { PermissionContextType, usePermissions } from "../../../modules/auth/Permissions/PermissionContext";
import { hasAccountBasedPermission, hasPermission } from "../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../modules/auth/Permissions/PermissionStrings";
import { EntityNames } from "../../../enums/EntityNames";

export interface IWritableList {
    entityName: string,
    i: any,
    filters: IFilter[],
    parentId: string | undefined,
    description?: string | undefined,
    actionOverrides?: Actions[],
    pollTime?: number | undefined,
    canAdd?: boolean | ((ctx: PermissionContextType, accountId: string | undefined) => boolean)
}

export const enum Actions {
    Add = "Add",
    Edit = "Edit",
    Delete = "Delete",
    Download = "Download",
}

export default function WritableList({ entityName, i, filters, parentId, description, actionOverrides, pollTime, canAdd: canAddProp }: IWritableList) {
    useSignals();

    const schema = GetSchema(entityName)
    const [data, setData] = useState<any>(undefined);
    const [resultsKey] = useState<string>(entityName);
    const [actions] = useState(actionOverrides ?? [Actions.Add, Actions.Edit, Actions.Delete]);
    const navigate = useNavigate();
    const permissionsContext = usePermissions();
    const { entityId } = useParams();

    function canAdd(): boolean {
        // If canAdd prop is provided from schema, use it
        if (canAddProp !== undefined) {
            return typeof canAddProp === 'function' ? canAddProp(permissionsContext!, entityId) : canAddProp;
        }

        // Fall back to existing logic for backward compatibility
        if (!entityId) {
            return false;
        }

        if (entityName === EntityNames.prefixes) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_Prefixes);
        }
        if (entityName === EntityNames.accountOrderNumbers) {
            return hasAccountBasedPermission(entityId, permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_OrderNumbers, permissionsContext.cellTrakOperatorClaim);
        }
        // For other entities, allow add if action is included (default behavior)
        return true;
    }

    function canEdit(): boolean {
        // Check for global entities that don't require entityId
        if (entityName === EntityNames.caseMaterialTypes) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Case_Material_Types);
        }
        if (entityName === EntityNames.deliveryMethods) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Delivery_Methods);
        }

        if (!entityId) {
            return false;
        }

        if (entityName === EntityNames.prefixes) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_Prefixes);
        }
        if (entityName === EntityNames.accountOrderNumbers) {
            return hasAccountBasedPermission(entityId, permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_OrderNumbers, permissionsContext.cellTrakOperatorClaim);
        }
        // For other entities, allow edit if action is included (default behavior)
        return true;
    }

    function canDelete(): boolean {
        // Check for global entities that don't require entityId
        if (entityName === EntityNames.caseMaterialTypes) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Case_Material_Types);
        }
        if (entityName === EntityNames.deliveryMethods) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Delivery_Methods);
        }

        if (!entityId) {
            return false;
        }

        if (entityName === EntityNames.prefixes) {
            return hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_Prefixes);
        }
        if (entityName === EntityNames.accountOrderNumbers) {
            return hasAccountBasedPermission(entityId, permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Manage_Account_OrderNumbers, permissionsContext.cellTrakOperatorClaim);
        }
        // For other entities, allow delete if action is included (default behavior)
        return true;
    }

    async function getData() {
        const payload = { filters };
        const result = await httpPost(`api/${entityName}/list`, payload);
        if (result?.data) {
            setData(result?.data);
        }
    }

    async function deleteEntity(id: string) {
        const payload = { id, parentId: parentId };
        const result = await httpDelete(`api/${entityName}/delete`, payload)
        if (result?.status === HttpStatusCode.Ok) {
            setData({ [resultsKey]: data[resultsKey].filter((x: any) => x.id != id) })

            // Check if result data contains packing box information and propagate to list
            if (result?.data?.packingBoxId && result?.data?.packingBoxTypeName) {
                globalUpdateResultsSignal.value = {
                    id: result.data.packingBoxId,
                    updateChanges: {
                        typeName: result.data.packingBoxTypeName
                    }
                };
            }
        }
    }

    function handleAddNew() {
        const navPayload = { parentId }
        Navigation.create.navigate(navigate, entityName, navPayload)
    }

    function handleEdit(id: string) {
        Navigation.edit.navigate(navigate, entityName, id)
    }

    function handleDeleteButtonPress(id: string) {
        modalSignal.value = <ConfirmModal heading={`Delete ${SingularLowerCase(schema.displayName)}?`} information="This action can not be undone" ContinueFunc={() => deleteEntity(id)} type={ConfirmType.Destructive} />
    }

    async function handleDownloadButtonPress(id: string, fileName: string) {
        var response = await httpGet(`api/${entityName}/downloadUrl?id=${id}`)
        var link = document.createElement("a");
        link.setAttribute('download', `${fileName}`);
        link.href = response.data.secureUri;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    useEffect(() => {
        getData();
        let interval: NodeJS.Timeout | undefined;
        if (pollTime !== undefined && pollTime !== null) {
            interval = setInterval(getData, pollTime);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [pollTime])

    useEffect(() => {
        const signal = globalUpdateResultsSignal.value;
        // Does the update affect a specific instance by id?
        if (data && signal.updateChanges && signal.id) {
            const updatedData = { ...data };
            const itemToUpdate = updatedData[resultsKey]?.find((x: any) => x.id === signal.id);
            if (itemToUpdate) {
                for (const key in signal.updateChanges) {
                    if (key in itemToUpdate) {
                        itemToUpdate[key] = signal.updateChanges[key];
                    }
                }
                setData(updatedData);
                // Don't clear the signal as side panel may also react to this change
            }
        }
    }, [globalUpdateResultsSignal.value])

    return (<>
        <TabPanel id={i} className={"flex__grow--0"}>
            <div className="display--flex justifyContent--space-between margin__bottom--s">
                <div>
                    {description && <p className="small small--2">{description}</p>}
                </div>

                {actions.includes(Actions.Add) && canAdd() &&
                    <Button className="button button--small button--primary button--with-icon" onPress={handleAddNew}>
                        <img src={iconPlus} />
                        <p>Add {SingularLowerCase(schema.displayName)}</p>
                    </Button>
                }
            </div>
        </TabPanel>

        <TabPanel id={i}>
            <div className="list__wrapper">
                <div className="list" data-testid="list-wrapper">
                    {data && data[resultsKey] !== undefined && data[resultsKey].length > 0 && renderListTable()}
                    {data && data[resultsKey] !== undefined && data[resultsKey].length === 0 && renderNoResultList()}
                </div>
            </div>
        </TabPanel>
    </>
    )

    function renderListTable() {
        return (
            <>
                <TableVirtuoso
                    className="react-aria-Table"
                    style={{}}
                    data={data[resultsKey]}
                    initialItemCount={data[resultsKey].length}
                    fixedHeaderContent={() => (<tr className="">
                        {schema.props.filter(x => x.showInList === true).map(prop => (<>
                            <th><p className="small">{prop.displayName}</p></th>
                        </>))}
                        <td></td>
                    </tr>)}
                    itemContent={(index, item) => (
                        <>
                            {schema.props.filter(x => x.showInList === true).map(prop => (
                                <td>
                                    {getCellComponent(item, prop, entityName)}
                                </td>
                            ))}

                            <td>
                                <div className="display--flex justifyContent--flex-end gap--xxs">
                                    {actions.includes(Actions.Delete) && canDelete() &&
                                        <Button className=" button button--small button--secondary button--secondary--destructive button--with-icon" onPress={() => handleDeleteButtonPress(item.id)}>
                                            <img src={iconDelete} />
                                            Delete
                                        </Button>
                                    }
                                    {actions.includes(Actions.Edit) && canEdit() &&
                                        <Button className="button button--small button--secondary button--with-icon" onPress={() => handleEdit(item.id)}>
                                            <img src={iconEdit} />
                                            Edit
                                        </Button>
                                    }
                                    {actions.includes(Actions.Download) &&
                                        <Button className="button button--small button--primary button--with-icon" onPress={() => handleDownloadButtonPress(item.id, item.fileName)} isDisabled={!item.readyForDownload} >
                                            <img src={iconDownload} />
                                            Download
                                        </Button>
                                    }
                                </div>
                            </td>
                        </>
                    )}
                />
            </>
        )
    }

    function renderNoResultList() {
        return (
            <div>
                <div className="react-aria-Table">
                    <table >
                        <th></th>
                        {schema.props.filter(x => x.showInList === true).map(prop => (<>
                            <th><p className="small"></p></th>
                        </>))}
                        <th></th>
                    </table>
                </div>
                {<div className="list--empty">
                    <div className="display--flex flex"><img src={iconInfoBlue} /></div>
                    <p>No results</p>
                </div>}
            </div>
        )
    }
}